import React, { useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";

import type {
  PaygapReportPdfLabels,
  ChartImages,
} from "../components/PaygapReportPdf/PaygapReportPdf";
import {
  useChartCapture,
  PAYGAP_CHART_IDS,
} from "../components/PaygapReportPdf/hooks/useChartCapture";
import {
  generateFallbackSummary,
} from "../components/PaygapReportPdf/components/ExecutiveSummary";
import type { ProcessedAnalysisData } from "./usePaygapAnalysisData";
import {
  getRegressionParametersByLang,
  getRegressionNoteLabel,
  getSalaryComponentLabel,
  getSalaryComponentTooltip,
} from "../../PaygapUploadData/hooks/regressionOptions";

import { useGetCurrentCompanyInfo, useDownloadFilePreview, useGetAIAssistance } from "@api";
import { useUserStore } from "@store/user";
import { useGetPaygapSettings } from "@api/hooks/paygap";

interface UsePaygapReportGeneratorReturn {
  generateReport: (analysisData: ProcessedAnalysisData) => Promise<void>;
  isGenerating: boolean;
  progress: number;
  error: string | null;
}

/**
 * Hook to orchestrate paygap PDF report generation
 */
export const usePaygapReportGenerator = (): UsePaygapReportGeneratorReturn => {
  const { t, i18n } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const { user } = useUserStore();
  const { currentCompanyInfo } = useGetCurrentCompanyInfo(user);
  const { downloadFilePreview } = useDownloadFilePreview(user);
  const { settings } = useGetPaygapSettings(user, true);
  const aiAssistance = useGetAIAssistance();
  const { captureChartById } = useChartCapture();

  const [isGenerating, setIsGenerating] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);

  const language = i18n.language === "cs" ? "cs" : i18n.language === "sk" ? "sk" : "en";
  const currencyCode = settings?.analysisParameters?.currency || "EUR";
  const regressionValue = String(settings?.analysisParameters?.regression || "1");
  const regressionParametersValue = getRegressionParametersByLang(regressionValue, language);

  const snackbarSuccess = t("paygap.report.snackbar.success", "Report downloaded successfully");
  const snackbarError = t("paygap.report.snackbar.error", "Failed to generate report. Please try again.");

  /**
   * Get all translation labels for the PDF
   */
  const getLabels = useCallback((): PaygapReportPdfLabels => ({
    reportTitle: t("paygap.report.title", "Gender Pay Gap Analysis Report"),
    reportSubtitle: t("paygap.report.subtitle", "Comprehensive Pay Equity Analysis"),
    generatedOn: t("paygap.report.generatedOn", "Generated on"),
    executiveSummary: t("paygap.report.executiveSummary", "Executive Summary"),
    keyFindings: t("paygap.report.keyFindings", "Key Findings"),
    keyMetrics: t("paygap.report.keyMetrics", "Key Metrics"),
    incomeAnalysis: t("paygap.report.incomeAnalysis", "Income by Quartile and Gender"),
    employeeDistribution: t("paygap.report.employeeDistribution", "Employee Distribution"),
    salaryCostAnalysis: t("paygap.report.salaryCostAnalysis", "Salary Cost Analysis"),
    payGapBySeniority: t("paygap.report.payGapBySeniority", "Pay Gap by Seniority"),
    payGapByPosition: t("paygap.report.payGapByPosition", "Pay Gap by Position/Role"),
    payGapByGrade: t("paygap.report.payGapByGrade", "Pay Gap by Grade"),
    statisticalIndicators: t("paygap.report.statisticalIndicators", "Statistical Indicators"),
    metric: t("paygap.report.metric", "Metric"),
    men: t("paygap.report.men", "Men"),
    women: t("paygap.report.women", "Women"),
    comparison: t("paygap.report.comparison", "Comparison"),
    gender: t("paygap.report.gender", "Gender"),
    totalHeadcount: t("paygap.report.totalHeadcount", "Total Headcount"),
    monthlyContracts: t("paygap.report.monthlyContracts", "Monthly Contracts"),
    hourlyContracts: t("paygap.report.hourlyContracts", "Hourly Contracts"),
    category: t("paygap.report.category", "Category"),
    totalCost: t("paygap.report.totalCost", "Total Cost"),
    sharePercent: t("paygap.report.sharePercent", "Share %"),
    total: t("paygap.report.total", "Total"),
    genderPayGap: t("paygap.report.genderPayGap", "Gender Pay Gap"),
    employees: t("paygap.report.employees", "Employees"),
    complianceRisk: t("paygap.report.complianceRisk", "Compliance Risk"),
    statisticallySignificant: t("paygap.report.statisticallySignificant", "Statistically Significant"),
    high: t("paygap.report.high", "High"),
    medium: t("paygap.report.medium", "Medium"),
    low: t("paygap.report.low", "Low"),
    yes: t("paygap.report.yes", "Yes"),
    no: t("paygap.report.no", "No"),
    discriminationCoefficient: t("paygap.report.discriminationCoefficient", "Discrimination Coefficient"),
    standardErrorCoefficient: t("paygap.report.standardErrorCoefficient", "Standard Error Coefficient"),
    totalDataRecords: t("paygap.report.totalDataRecords", "Total Data Records"),
    employeesInAnalysis: t("paygap.report.employeesInAnalysis", "Employees in Analysis"),
    notInAnalysis: t("paygap.report.notInAnalysis", "Not in Analysis"),
    validEmployees: t("paygap.report.validEmployees", "Valid Employees"),
    excludedTraining: t("paygap.report.excludedTraining", "Excluded (Training)"),
    zeroIncome: t("paygap.report.zeroIncome", "Zero Income"),
    invalidRecords: t("paygap.report.invalidRecords", "Invalid Records"),
    degreesOfFreedom: t("paygap.report.degreesOfFreedom", "Degrees of Freedom"),
    testHypothesis: t("paygap.report.testHypothesis", "Test Hypothesis"),
    criticalValue: t("paygap.report.criticalValue", "Critical Value"),
    testStatistic: t("paygap.report.testStatistic", "Test Statistic"),
    confidential: t("paygap.report.confidential", "Confidential"),
    page: t("paygap.report.page", "Page"),
    of: t("paygap.report.of", "of"),
    chartNotAvailable: t("paygap.report.chartNotAvailable", "Chart not available"),
    gradingI: t("paygap.report.gradingI", "Grading I"),
    regressionParametersLabel: t("paygap.report.regressionParametersLabel", getRegressionNoteLabel(language)),
    regressionParametersValue: regressionParametersValue,
    gradeLabel: t("paygap.report.grade", "Grade"),
    payGapLabel: t("paygap.report.payGapPercent", "Pay Gap %"),
    netSalaryLabel: getSalaryComponentLabel("net", language),
    basicSalaryLabel: getSalaryComponentLabel("basic", language),
    variableSalaryLabel: getSalaryComponentLabel("variable", language),
    variableTooltipLabel: getSalaryComponentTooltip("variable", language),
  }), [t, regressionParametersValue, language]);

  /**
   * Fetch company logo as data URL
   */
  const fetchCompanyLogo = useCallback(async (): Promise<string | null> => {
    if (!currentCompanyInfo?.appearance?.logo?.path) {
      return null;
    }

    try {
      const blob = await downloadFilePreview({
        path: currentCompanyInfo.appearance.logo.path,
        name: currentCompanyInfo.appearance.logo.name,
      });


return new Promise((resolve) => {
        const reader = new FileReader();

        reader.onloadend = () => resolve(reader.result as string);
        reader.readAsDataURL(blob);
      });
    } catch (err) {
      console.warn("Failed to fetch company logo:", err);

return null;
    }
  }, [currentCompanyInfo, downloadFilePreview]);

  /**
   * Capture all chart images
   */
  const captureAllCharts = useCallback(async (): Promise<ChartImages> => {
    const chartImages: ChartImages = {};

    const chartIds = [
      { id: PAYGAP_CHART_IDS.payDifferenceGauge, key: "payDifferenceGauge" },
      { id: PAYGAP_CHART_IDS.incomeQuartileBoxPlot, key: "incomeQuartileBoxPlot" },
      { id: PAYGAP_CHART_IDS.employeeDistributionDonut, key: "employeeDistributionDonut" },
      { id: PAYGAP_CHART_IDS.seniorityPayGapChart, key: "seniorityPayGapChart" },
      { id: PAYGAP_CHART_IDS.positionRoleChart, key: "positionRoleChart" },
      { id: PAYGAP_CHART_IDS.gradePayGapChart, key: "gradePayGapChart" },
      { id: PAYGAP_CHART_IDS.gradePayGapChartGrading1, key: "gradePayGapChartGrading1" },
    ];

    for (const { id, key } of chartIds) {
      try {
        const result = await captureChartById(id);

        if (result) {
          (chartImages as Record<string, string>)[key] = result.dataUrl;
        }
      } catch (err) {
        console.warn(`Failed to capture chart ${id}:`, err);
      }
    }

    return chartImages;
  }, [captureChartById]);

  /**
   * Generate AI summary text
   */
  const generateAiSummary = useCallback(
    async (analysisData: ProcessedAnalysisData): Promise<string | undefined> => {
      const { payDifferenceData, analysisSummary, unadjustedPayGap, employeeDistributionData } =
        analysisData;

      // Build AI request
      const aiRequest = {
        question: t("paygap.report.aiPrompt.question"),
        description: JSON.stringify({
          genderPayGap: payDifferenceData.genderCoefficient,
          unadjustedPayGap,
          employeeCount: analysisSummary.employeeRecords,
          complianceRisk: analysisSummary.complianceRisk,
          isSignificant: payDifferenceData.isSignificant,
          menCount: employeeDistributionData[0]?.totalHeadcount || 0,
          womenCount: employeeDistributionData[1]?.totalHeadcount || 0,
        }),
        companyName: currentCompanyInfo?.name || "",
        language,
        aiPrompt: "paygap-report-summary",
        userInput: "",
      };

      return new Promise((resolve) => {
        aiAssistance.mutate(aiRequest, {
          onSuccess: (result) => {
            resolve(result);
          },
          onError: (err) => {
            console.warn("AI summary generation failed:", err);
            // Return fallback summary
            resolve(
              generateFallbackSummary(
                payDifferenceData.genderCoefficient,
                analysisSummary.employeeRecords,
                analysisSummary.complianceRisk,
                payDifferenceData.isSignificant,
                language,
                t,
              ),
            );
          },
        });
      });
    },
    [t, currentCompanyInfo, language, aiAssistance],
  );

  /**
   * Main report generation function
   */
  const generateReport = useCallback(
    async (analysisData: ProcessedAnalysisData): Promise<void> => {
      setIsGenerating(true);
      setProgress(0);
      setError(null);

      try {
        const companyName = currentCompanyInfo?.name || t("paygap.report.unknownCompany");
        const generatedDate = new Date().toLocaleDateString(language, {
          year: "numeric",
          month: "long",
          day: "numeric",
        });

        // Step 1: Fetch company logo (10%)
        setProgress(10);
        const companyLogo = await fetchCompanyLogo();

        // Step 2: Capture chart images (40%)
        setProgress(20);
        const chartImages = await captureAllCharts();

        setProgress(40);

        // Step 3: Generate AI summary (60%)
        setProgress(50);
        let aiSummary: string | undefined;

        try {
          aiSummary = await generateAiSummary(analysisData);
        } catch {
          // Use fallback summary
          aiSummary = generateFallbackSummary(
            analysisData.payDifferenceData.genderCoefficient,
            analysisData.analysisSummary.employeeRecords,
            analysisData.analysisSummary.complianceRisk,
            analysisData.payDifferenceData.isSignificant,
            language,
          );
        }
        setProgress(60);

        // Step 4: Get labels
        const labels = getLabels();

        // Step 5: Generate PDF (80%) - Dynamic import to avoid loading heavy library on page load
        setProgress(70);
        const [{ pdf }, { default: PaygapReportPdf }] = await Promise.all([
          import("@react-pdf/renderer"),
          import("../components/PaygapReportPdf/PaygapReportPdf"),
        ]);

        const pdfDocument = (
          <PaygapReportPdf
            companyName={companyName}
            companyLogo={companyLogo}
            generatedDate={generatedDate}
            analysisData={analysisData}
            aiSummary={aiSummary}
            chartImages={chartImages}
            labels={labels}
            currencyCode={currencyCode}
            language={language}
          />
        );

        setProgress(80);
        const blob = await pdf(pdfDocument).toBlob();

        // Step 6: Download PDF (100%)
        setProgress(90);
        const downloadLink = document.createElement("a");

        downloadLink.href = URL.createObjectURL(blob);
        downloadLink.download = `${companyName.replace(/[^a-zA-Z0-9]/g, "_")}_PayGap_Report_${new Date().toISOString().split("T")[0]}.pdf`;
        downloadLink.click();
        URL.revokeObjectURL(downloadLink.href);

        setProgress(100);
        enqueueSnackbar(snackbarSuccess, { variant: "success" });
      } catch (err) {
        console.error("Report generation failed:", err);
        setError(err instanceof Error ? err.message : t("paygap.common.unknownError", "Unknown error"));
        enqueueSnackbar(snackbarError, { variant: "error" });
      } finally {
        setIsGenerating(false);
      }
    },
    [
      currentCompanyInfo,
      t,
      language,
      currencyCode,
      fetchCompanyLogo,
      captureAllCharts,
      generateAiSummary,
      getLabels,
      enqueueSnackbar,
    ],
  );

  return {
    generateReport,
    isGenerating,
    progress,
    error,
  };
};

export type { UsePaygapReportGeneratorReturn };
